/**
 * <biosphere-coa> — opens the certificates in a dialog instead of sending the buyer to
 * a bare image on another tab.
 *
 * The links are both the baseline and the triggers: every flavour entry is a real
 * anchor to its own certificate, so the panel works with scripting off. This
 * intercepts the click and shows the same document in place.
 *
 * The dialog is the ported biosphere-dialog, opened through its own trigger rather
 * than by reaching into its <dialog>.
 */
class BiosphereCoa extends HTMLElement {
  #entries = [];
  #tabs = [];
  #thumb;
  #full;
  #flavour;
  #open;
  #trigger;
  #abort;

  connectedCallback() {
    this.#entries = [...this.querySelectorAll("[data-coa-entry]")];
    this.#thumb = this.querySelector("[data-coa-thumb]");
    this.#tabs = [...this.querySelectorAll("[data-coa-tab]")];
    this.#full = this.querySelector("[data-coa-full]");
    this.#flavour = this.querySelector("[data-coa-flavour]");
    this.#open = this.querySelector("[data-coa-open]");
    this.#trigger = this.querySelector(".biosphere-dialog__trigger");

    // Without the dialog the anchors stand, which is the baseline and is
    // not broken — so there is nothing to repair, only nothing to add.
    if (!this.#full || !this.#trigger || this.#entries.length === 0) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#entries.forEach((entry, index) => {
      entry.addEventListener("click", (event) => this.#onOpen(event, index), {
        signal,
      });
    });

    // The thumbnail is not an entry — it is a second way into the first one, so it
    // stays out of #entries and the indices keep matching the flavours.
    this.#thumb?.addEventListener("click", (event) => this.#onOpen(event, 0), {
      signal,
    });

    this.#tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => this.#show(index), { signal });
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** Open the dialog on one certificate, leaving modified clicks to the browser. */
  #onOpen(event, index) {
    // Modified clicks still mean "open this document however I asked" — a new tab,
    // a new window, a download.
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;

    event.preventDefault();
    this.#show(index);
    this.#trigger.click();
  }

  /** Show one certificate: the document, its name, and where it opens. */
  #show(index) {
    const entry = this.#entries[index];
    if (!entry) return;

    const url = entry.getAttribute("href");

    // The label as Liquid wrote it, not the entry's text: that also
    // carries the visually-hidden note about opening a new window, which
    // is true of the link and not part of the flavour's name.
    const label = entry.dataset.coaLabel ?? "";

    this.#full.src = url;

    // The new certificate's own shape, so the box does not hold the last
    // one's while this one loads.
    if (entry.dataset.coaWidth) this.#full.width = entry.dataset.coaWidth;
    if (entry.dataset.coaHeight) this.#full.height = entry.dataset.coaHeight;

    // The alt names which certificate this is, through the sentence
    // Liquid translated — the flavour is the only part of it that
    // changes. A product with one certificate has no flavour to name, and
    // keeps the alt it was rendered with.
    if (label && this.dataset.altTemplate) {
      this.#full.alt = this.dataset.altTemplate.replace("[flavour]", label);
    }

    if (this.#flavour) this.#flavour.textContent = label;
    if (this.#open) this.#open.href = url;

    this.#tabs.forEach((tab, position) => {
      if (position === index) {
        tab.setAttribute("aria-current", "true");
      } else {
        tab.removeAttribute("aria-current");
      }
    });
  }
}

if (!customElements.get("biosphere-coa")) {
  customElements.define("biosphere-coa", BiosphereCoa);
}
